import { Inject, Injectable } from "@angular/core";
import {
  HttpClient,
  HttpDownloadProgressEvent,
  HttpEvent,
  HttpEventType,
} from "@angular/common/http";
import { defer, EMPTY, from, Observable, of } from "rxjs";
import { catchError, concatMap, map, retry } from "rxjs/operators";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { NovaDeviceService } from "./nova-device.service";
import {
  BufferBand,
  ChatHistoryTurn,
  ChatMessage,
  NovaResolvedNav,
} from "./nova-companion-chat.model";
import {
  NovaWalkthroughEvent,
  NovaWalkthroughSliceResponse,
  NovaWalkthroughState,
  NovaWalkthroughStep,
} from "./nova-realtime.model";

interface ChatContextResponse {
  firstName: string;
  bufferBand: BufferBand;
  conversationIdleMinutes: number;
  /**
   * FLIP-278. Whether voice mode should open a Realtime session or use the current
   * SendMessageStream + Speak path.
   *
   * Comes from the server for the same reason conversationIdleMinutes does: one value, held by the
   * side that enforces it. The App Setting behind it is what lets a bad Realtime session be turned
   * off for every client in one restart, which only works while no build-time copy of it sits in
   * front - see the note on getRealtimeEnabled.
   */
  realtimeEnabled: boolean;
}

interface ChatHistoryResponse {
  turns: ChatHistoryTurn[];
}

/**
 * One frame of a streamed reply. Mirrors NovaSendMessageChunk on the server.
 *
 * `replace` is the interesting one: it means throw away everything shown so far and use this
 * instead. A model can decline part-way through, and on this product the fallback that carries
 * the crisis resources has to win outright rather than be appended to half an answer.
 */
export interface ReplyChunk {
  delta?: string;
  replace?: string;
  done?: boolean;
  safetyEscalationTriggered?: boolean;
  /** FLIP-230. Server-side latency breakdown. Present on the final frame only. */
  timings?: TurnTimings;
  /**
   * A navigation target the server resolved to a routable id. Final frame only, and absent on
   * most turns - see NovaResolvedNav for what its absence means.
   */
  nav?: NovaResolvedNav;
  /**
   * Final frame only, and only on a turn that began a walkthrough: the steps for its first
   * location, for the walkthrough service to hold. The same side-band a spoken session gets from
   * begin_walkthrough, so a typed walkthrough runs through the same client state.
   */
  walkthroughSteps?: NovaWalkthroughStep[];
  /** The next location to prefetch, alongside `walkthroughSteps`. */
  walkthroughNextArea?: string;
  /**
   * Final frame only: changes to the walkthrough the model asked for this turn (next step, pause,
   * resume, skip, end), for the walkthrough service to apply to the state it holds.
   */
  walkthroughEvents?: NovaWalkthroughEvent[];
}

/**
 * FLIP-230. Where one turn's SERVER-side time went. Mirrors NovaTurnTimings on the server.
 *
 * The client can time the round trip perfectly well on its own, and that number says only "it was
 * slow". These fields say which stage was slow, which is the question the ticket asks: a cold
 * process, a cold prompt cache and a slow database all look identical from out here.
 *
 * All values are milliseconds except the two that are not, which say so in their names.
 */
export interface TurnTimings {
  /** Seconds this server process had been up. The cold-start discriminator. */
  processUptimeSeconds: number;
  /** Which turn this was for the server process, counting from 1. */
  turnIndex: number;
  digestMs: number;
  conversationMs: number;
  historyMs: number;
  prepareMs: number;
  /** Time to first token. The number this ticket is really about. */
  firstTokenMs: number;
  completionMs: number;
  /** Whether a voice warm-up ran for this session before the turn. */
  warmed: boolean;
  systemPromptCharacters: number;
}

/** FLIP-230. What a voice-session warm-up managed to prime. */
export interface WarmUpResponse {
  enabled: boolean;
  digestPrimed: boolean;
  promptCachePrimed: boolean;
  speechPrimed: boolean;
  elapsedMs: number;
}

interface SendMessageResponse {
  reply: string;
  safetyEscalationTriggered: boolean;
}

interface OpeningChipsResponse {
  chips: string[];
}

/**
 * Real Companion data (FLIP-202). NovaCompanionChatComponent only knows about this service's
 * method signatures - it has no idea replies now come from a server-side Claude call rather than
 * a canned string.
 *
 * The LLM call is entirely server-side: this calls our own backend, which assembles the user's
 * digest and calls Claude. Per nova-prototype-file-guidance.md §5 the browser must never call a
 * model vendor directly, so nothing here knows about prompts, digests or API keys.
 *
 * FLIP-255 added one exception to §5, and it does NOT apply to this service. A Realtime voice
 * session opens a WebRTC connection to OpenAI from the browser using a short-lived session-scoped
 * secret minted by POST /api/Nova/StartRealtimeSession; the account key still never leaves the
 * server, and the prompt is baked into the minted session rather than sent to the page. This
 * service remains the CURRENT text and voice path — SendMessageStream plus Speak — which is not
 * deprecated and stays fully selectable behind the Nova:Realtime:Enabled flag.
 *
 * The reply is returned RAW, including any [[NAV:...]] / [[SAFETY_ESCALATION_TRIGGERED]] tags -
 * the component parses and strips them, so stripping here would break navigation and the safety
 * signal.
 *
 * Uses the direct-HttpClient + API_BASE_URL pattern rather than the NSwag-generated client,
 * matching NovaInsightSettingsService and NovaCompanionSettingsService.
 */
@Injectable({
  providedIn: "root",
})
export class NovaCompanionChatService {
  /**
   * Shown when the backend can't be reached. Deliberately says nothing about the user's data:
   * inventing an observation about someone's week when we couldn't load their week would be
   * fabricating a personal health claim. It also carries no [[NAV:...]] tag, since suggesting a
   * destination we didn't reason about would be equally invented.
   */
  private readonly connectionErrorReply =
    "I'm having trouble reaching my end of things right now, so I can't give you a proper " +
    "response. Please try again in a moment. If something feels urgent, Lifeline is on " +
    "13 11 14 and Beyond Blue on 1300 22 4636, any time.";

  /**
   * Generic conversation starters, used when chip generation is unavailable. Safe to fall back
   * to because they're generic prompts rather than claims about the user - they're drawn from
   * the chips prompt's own documented fallback list.
   */
  private readonly fallbackChips: string[] = [
    "How's my Buffer looking this week?",
    "I've been finding things tough lately.",
    "Help me think about tomorrow.",
    "What have my Stars been this week?",
  ];

  /**
   * Used only when the context call fails and the server's value never arrived. Matches
   * NovaSettings.CompanionConversationIdleMinutes so a degraded client draws its divider in the
   * same place the server would have; if that default ever changes, change it in both.
   */
  private readonly defaultIdleMinutes = 30;

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    private device: NovaDeviceService
  ) {}

  /** First name for the greeting message. Empty string falls back to a name-less greeting. */
  getUserFirstName(): Observable<string> {
    return this.getChatContext().pipe(map((context) => context.firstName ?? ""));
  }

  /** Current Safety Buffer band, for the chat header's colour treatment. */
  getBufferBand(): Observable<BufferBand> {
    return this.getChatContext().pipe(map((context) => context.bufferBand ?? "no-score"));
  }

  /**
   * Minutes of silence after which the next message begins a new conversation.
   *
   * Comes from the server rather than being a constant here, because the server independently
   * applies the same rule when it stores the turn. Two copies of the number would drift the first
   * time it is retuned, and the divider the reader sees would stop matching the one recorded.
   */
  getConversationIdleMinutes(): Observable<number> {
    return this.getChatContext().pipe(
      map((context) => context.conversationIdleMinutes || this.defaultIdleMinutes)
    );
  }

  /**
   * FLIP-278. Which voice path this client should open: Realtime, or the current
   * SendMessageStream + Speak one.
   *
   * Read here rather than from `environment`, and the build-time `novaRealtimeEnabled` flag that
   * used to gate the support probe is gone rather than combined with this. The whole value of the
   * `Nova:Realtime:Enabled` App Setting is that flipping it moves every client without a release -
   * and flipping it BACK is how a bad Realtime session gets people onto a working voice path in one
   * restart. A build-time copy in front of it would defeat both halves: production ships false, so
   * the App Setting would do nothing until somebody cut a release.
   *
   * The server enforces the same flag independently - StartRealtimeSession and RealtimeEvent both
   * return 503 when it is off - so this is what to ASK before opening voice mode, not what makes
   * Realtime safe. Asking first only means the answer arrives before the user has committed to a
   * call rather than after.
   *
   * False means the current voice path, which is not deprecated and stays fully selectable. Null
   * means the server could not be asked (FLIP-486) - not the same thing as false.
   */
  getRealtimeEnabled(): Observable<boolean | null> {
    // FLIP-486. NOT getChatContext(): that one folds a failure into `realtimeEnabled: false`, and
    // "the server said off" and "the server never answered" are different facts. The first-voice-
    // of-the-day failure was exactly this conflation - the API's first request after an idle spell
    // is slow or fails, the failure became `false`, and voice quietly opened the legacy path with
    // no "Connecting" state. Retried once because a cold API usually answers the second time; null
    // after that means "unknown", which the caller must say out loud rather than guess at.
    return this.http
      .get<ChatContextResponse>(`${this.apiBaseUrl}/api/Nova/GetChatContext`)
      .pipe(
        retry(1),
        map((context) => context.realtimeEnabled === true),
        catchError(() => of(null as boolean | null))
      );
  }

  /**
   * Turns from earlier conversations, oldest first, for the scrollback above the current exchange.
   *
   * NEVER pass the result to sendMessage. It is read-only history; the outgoing conversation is
   * only what has been said since this conversation began. An empty array on failure means the
   * sheet opens without history rather than not opening.
   */
  getChatHistory(): Observable<ChatHistoryTurn[]> {
    return this.http
      .get<ChatHistoryResponse>(`${this.apiBaseUrl}/api/Nova/GetChatHistory`)
      .pipe(
        map((response) => response?.turns ?? []),
        catchError(() => of([] as ChatHistoryTurn[]))
      );
  }

  /** 4 opening conversation-starter chips shown before the user's first message. */
  getOpeningChips(): Observable<string[]> {
    return this.http
      .get<OpeningChipsResponse>(`${this.apiBaseUrl}/api/Nova/GetOpeningChips`)
      .pipe(
        map((response) =>
          response?.chips?.length ? response.chips : this.fallbackChips
        ),
        catchError(() => of(this.fallbackChips))
      );
  }

  /**
   * @param conversationHistory Full conversation so far, including the just-added user message -
   *   sent as-is, mirroring the handoff's contract. Any leading Nova greeting is dropped
   *   server-side, since the API requires the first message to come from the user.
   * @param voice True when the reply is going to be SPOKEN. Adds the spoken register to the
   *   system prompt server-side - shorter turns, no lists, written to be heard rather than read.
   *   The server cannot work this out for itself: the same user on the same endpoint may be
   *   typing or talking, and can switch mid-conversation.
   * @returns raw assistant reply text, including any [[NAV:...]] or
   *   [[SAFETY_ESCALATION_TRIGGERED]] tags - the component parses those out.
   */
  sendMessage(conversationHistory: ChatMessage[], voice = false): Observable<string> {
    return this.http
      .post<SendMessageResponse>(`${this.apiBaseUrl}/api/Nova/SendMessage`, {
        messages: conversationHistory,
        voice,
      })
      .pipe(
        map((response) => response?.reply ?? this.connectionErrorReply),
        catchError(() => of(this.connectionErrorReply))
      );
  }

  /**
   * The same turn as sendMessage, delivered as Nova writes it.
   *
   * Why this exists is latency, not novelty: with the buffered call the first sentence cannot be
   * sent for synthesis until the last one has been written, and the user spends that whole time
   * listening to nothing.
   *
   * Angular's HttpClient cannot hand back a stream, but with `reportProgress` it re-emits the
   * body-so-far on every packet, and the server writes one JSON object per line. So the parsing
   * job is: take whatever WHOLE lines have arrived since last time, and remember how far we read.
   * EventSource would be the obvious alternative and cannot be used - it is GET-only and cannot
   * carry the bearer token this endpoint needs.
   */
  sendMessageStream(
    conversationHistory: ChatMessage[],
    voice = false,
    walkthrough: NovaWalkthroughState | null = null
  ): Observable<ReplyChunk> {
    return defer(() => {
      // Per subscription, not per service: two overlapping streams would otherwise share a
      // read position and eat each other's frames.
      let consumed = 0;
      // Read per message, so a resize between messages is answered for the layout it has now.
      const device = this.device.current();

      return this.http
        .post(
          `${this.apiBaseUrl}/api/Nova/SendMessageStream`,
          // Only when there is a walkthrough to report: absent means none, and the server adds no
          // context for it. Likewise the device: absent means unknown, and a route is given for both.
          {
            messages: conversationHistory,
            voice,
            ...(walkthrough ? { walkthrough } : {}),
            ...(device ? { device } : {}),
          },
          { observe: "events", responseType: "text", reportProgress: true }
        )
        .pipe(
          concatMap((event: HttpEvent<string>) => {
            const text = this.streamedTextOf(event);
            if (text === null) return EMPTY;

            // Only up to the last newline: a trailing partial line is half a JSON object, and
            // will be complete on the next packet.
            const boundary = text.lastIndexOf("\n");
            if (boundary < consumed) return EMPTY;

            const ready = text.slice(consumed, boundary);
            consumed = boundary + 1;
            return from(this.parseChunks(ready));
          }),
          // Same fallback as the buffered call: a dead connection reads as Nova saying something
          // human rather than as an error, and replaces rather than appends.
          catchError(() => of({ replace: this.connectionErrorReply, done: true } as ReplyChunk))
        );
    });
  }

  /**
   * One location's steps of a walkthrough, fetched ahead of need so crossing into it is instant.
   * The typed-chat counterpart of the relay's prefetch, which needs a live voice session this path
   * does not have. Null on any failure: a missed prefetch costs a slower crossing, never a broken
   * walkthrough, so this does not retry.
   */
  getWalkthroughSlice(walkthroughId: string, location: string): Promise<NovaWalkthroughSliceResponse | null> {
    return this.http
      .post<NovaWalkthroughSliceResponse>(`${this.apiBaseUrl}/api/Nova/WalkthroughSlice`, {
        walkthroughId,
        location,
      })
      .toPromise()
      .then((response) => response ?? null)
      .catch(() => null);
  }

  /** The body so far, from whichever event carries it. Null for events that carry none. */
  private streamedTextOf(event: HttpEvent<string>): string | null {
    if (event.type === HttpEventType.DownloadProgress) {
      return (event as HttpDownloadProgressEvent).partialText ?? "";
    }
    if (event.type === HttpEventType.Response) {
      return event.body ?? "";
    }
    return null;
  }

  /**
   * Whole lines to chunks. An unparseable line is dropped rather than thrown on: mid-reply is the
   * worst moment to abandon a turn, and the user would hear Nova stop rather than see an error.
   */
  private parseChunks(lines: string): ReplyChunk[] {
    return lines
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        try {
          return JSON.parse(line) as ReplyChunk;
        } catch {
          return null;
        }
      })
      .filter((chunk): chunk is ReplyChunk => chunk !== null);
  }

  /**
   * FLIP-158. Synthesises one chunk of an already-generated reply and resolves to playable audio.
   *
   * Errors are NOT swallowed into a fallback the way sendMessage's are. A silent failure here
   * would leave voice mode waiting forever on audio that never arrives, so the caller needs to
   * see the failure and end the turn.
   *
   * @param text One sentence-sized chunk, already stripped of [[NAV:...]] /
   *   [[SAFETY_ESCALATION_TRIGGERED]] tags by the component.
   */
  speak(text: string): Observable<Blob> {
    return this.http.post(`${this.apiBaseUrl}/api/Nova/Speak`, { text }, { responseType: "blob" });
  }

  /**
   * FLIP-230. Asks the server to do a voice session's first-turn-only work now.
   *
   * Called when voice mode OPENS rather than when the first message is sent, because the gap
   * between those two moments is the user drawing breath and it is otherwise wasted. The server
   * spends it priming the digest, the prompt cache and the connections to OpenAI - all of which
   * the first turn would otherwise pay for while the user waits for Nova to say something.
   *
   * Errors are swallowed into a disabled-looking result on purpose. Nothing waits on this and
   * nothing breaks without it: a failed warm-up means the first turn is as slow as it was before,
   * which is a missed optimisation rather than a broken voice session.
   */
  warmUp(): Observable<WarmUpResponse> {
    return this.http
      .post<WarmUpResponse>(`${this.apiBaseUrl}/api/Nova/WarmUp`, {})
      .pipe(
        catchError(() =>
          of({
            enabled: false,
            digestPrimed: false,
            promptCachePrimed: false,
            speechPrimed: false,
            elapsedMs: 0,
          })
        )
      );
  }

  /**
   * Neutral defaults on failure: an empty name yields a name-less greeting and "green" is the
   * neutral header treatment. Showing a stale or invented Buffer colour would misrepresent the
   * user's current state, which the header is meant to communicate accurately.
   */
  private getChatContext(): Observable<ChatContextResponse> {
    return this.http
      .get<ChatContextResponse>(`${this.apiBaseUrl}/api/Nova/GetChatContext`)
      .pipe(
        catchError(() =>
          of({
            firstName: "",
            bufferBand: "no-score" as BufferBand,
            conversationIdleMinutes: this.defaultIdleMinutes,
            // False on purpose, and for the same reason as the other neutral defaults here: a
            // client that could not reach the server has no business negotiating a WebRTC session
            // against it. The legacy path is the one that still works from a degraded start.
            realtimeEnabled: false,
          })
        )
      );
  }
}
